import { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  GetEmployeesWithAvatarsDocument,
  JobCatalogPersonConnectionDocument,
  JobCatalogPersonConnectionQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import {
  NODE_UUID_TYPES,
  NodeUUIDType,
  getChildNodeType,
  getNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

type PersonData = JobCatalogPersonConnectionQuery['jobCatalog']['peopleConnection']['nodes'][0]

export interface JobCatalogPerson {
  employeeId: string
  employeeAccessId: string
  employeeFullName: string
  employeeGender?: string
  nodeGroupId?: string
  nodeGroupName?: string
  roleName: string
  levelName: string
}

export const useJobCatalogPeople = (id: string) => {
  const [people, setPeople] = useState<JobCatalogPerson[]>([])
  const nodeType = useMemo(() => getNodeType(id), [id])
  const childType = useMemo(() => getChildNodeType(id), [id])
  const isLevel = useMemo(() => nodeType === NODE_UUID_TYPES.LEVEL, [nodeType])

  const { data: employeesData } = useQuery(GetEmployeesWithAvatarsDocument)
  const avatars = useMemo(() => {
    const userMap: Record<string, string | undefined> = {}
    employeesData?.employees.employees.forEach((employee) => {
      if (employee.access) {
        userMap[employee.access.id] = employee.access.avatar ?? undefined
      }
    })
    return userMap
  }, [employeesData])

  const source = useGraphqlDataCollectionSource(
    {
      document: JobCatalogPersonConnectionDocument,
      totalItemSummary: (totalItems: number) =>
        i18n.tp('job_catalog.nodes.people.total_item_summary', { count: totalItems }),
      mapData: (data) => {
        const records = data.jobCatalog.peopleConnection.nodes.map((person) => {
          return {
            employeeId: person.employeeId,
            employeeAccessId: person.employeeAccessId ?? '0',
            employeeFullName: person.employeeFullName,
            employeeGender: person.employeeGender ?? '',
            roleName: person.jobcatalogtreeRole?.name ?? '-',
            levelName: person.jobcatalogtreeLevel?.name ?? '-',
            ...calculateNodeGrouping(person, childType),
          }
        })
        setPeople(records)
        return records
      },
      mapVariables: ({ search }) => ({
        search,
        nodeUuid: id,
      }),
      mapPagination: (data) => ({
        total: data.jobCatalog.peopleConnection.totalCount ?? 0,
      }),
      itemUrl: (item) => `/employees/${item.employeeId}`,
    },
    [nodeType]
  )

  return {
    people,
    source,
    avatars,
    isLevel,
  }
}

const calculateNodeGrouping = (person: PersonData, nodeUuid: NodeUUIDType) => {
  switch (nodeUuid) {
    case NODE_UUID_TYPES.FAMILY:
      return {
        nodeGroupId: person.jobcatalogtreeFamily?.uuid ?? '',
        nodeGroupName: person.jobcatalogtreeFamily?.name ?? '',
      }
    case NODE_UUID_TYPES.FUNCTION:
      return {
        nodeGroupId: person.jobcatalogtreeFunction?.uuid ?? '',
        nodeGroupName: person.jobcatalogtreeFunction?.name ?? '',
      }
    case NODE_UUID_TYPES.ROLE:
      return {
        nodeGroupId: person.jobcatalogtreeRole?.uuid ?? '',
        nodeGroupName: person.jobcatalogtreeRole?.name ?? '',
      }
    case NODE_UUID_TYPES.LEVEL:
      return {
        nodeGroupId: person.jobcatalogtreeLevel?.uuid ?? '',
        nodeGroupName: person.jobcatalogtreeLevel?.name ?? '',
      }
    default:
      return {
        nodeGroupId: person.jobcatalogtreeLevel?.uuid ?? '',
        nodeGroupName: person.jobcatalogtreeLevel?.name ?? '',
      }
  }
}
